/**
 * Inbound mail already linked to an RFQ — the supplier's answer, whatever form it took.
 *
 * ---- Why this exists ----
 *
 * A supplier replies to an RFQ and Frappe links the mail to it automatically: the `[<enquiry-id>]`
 * tag our subjects carry (`buildRfqDrafts`, `buildReminderDraft`) is enough for it to set
 * `reference_doctype: "RFQ"` / `reference_name: "RFQ-…"` on the `Communication`. Verified live
 * 2026-08-07 on a real reply — no `in_reply_to` header was involved.
 *
 * Nothing in Bosun read that. So a supplier could answer "Sorry no stock", the mail would sit
 * correctly linked to the RFQ, and the Supplier Quotations screen would still show **Awaiting**
 * for ever — with a Remind button offering to chase someone who had already answered.
 *
 * ---- Why `/api/resource` and not a `quoflow.*` method ----
 *
 * There isn't one. `mail.get_emails` filters by FOLDER, not by linked document, and no method in
 * the collection returns "mail attached to this RFQ". So this joins the seven other features
 * documented in `client.ts`/`frappe.ts` that still read through Frappe's generic resource API.
 * Filed as an ask; when a method appears, only `queryFn` changes.
 *
 * ---- What this deliberately does NOT do ----
 *
 * It does not parse anything, and it does not decide what a reply MEANS. A reply is shown to the
 * purchaser; turning one into a `Supplier Quotation` is an explicit act
 * (`useParseSupplierReply`). That separation is the whole point:
 * `supplier_quotation.parse_supplier_reply` run against "Sorry no stock" produced a quotation
 * containing the RFQ's line at **rate 0.00** and reported `matched: 1` — a decline recorded as an
 * offer. Until it can tell those apart (ask `E12`), a human decides.
 */

import { useQuery } from '@tanstack/react-query'
import { getResource } from './frappe'

/** One inbound mail linked to an RFQ. */
export interface RfqReply {
  /** `Communication.name` — also its id in `mail.get_email` (`name` and `id` are the same value
   *  on every row; verified across the live inbox). */
  id: string
  subject: string
  /** The address that sent it — NOT necessarily the address we mailed. A supplier replying from a
   *  colleague's account is normal, so this is shown, never used to match. */
  sender: string
  /** `"YYYY-MM-DD HH:mm:ss"`, site-local (contract §1) — not ISO-8601 UTC. */
  received_at: string
  seen: boolean
  /** A one-line preview. `Communication.content` is HTML; the caller renders text. */
  preview: string
  /**
   * TRUE when the SERVER linked this mail to the RFQ (`reference_name`), false when we matched it
   * ourselves because it names the RFQ. The distinction is worth keeping: linked is certainty,
   * matched is a very strong inference, and only the second can ever be wrong.
   */
  linked?: boolean
}

interface WireCommunication {
  name?: string
  subject?: string
  sender?: string
  communication_date?: string
  seen?: number | boolean
  content?: string
  /** The RFQ this mail answers. Only read by the GROUPED query below, which asks about many RFQs
   *  at once and so cannot infer it from the request. */
  reference_name?: string
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '')

/** Cheap HTML → one-line preview. Not `htmlToText`: this wants a snippet, not a faithful body. */
function preview(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim()
}

export function toRfqReply(w: WireCommunication): RfqReply {
  return {
    id: str(w.name),
    subject: str(w.subject),
    sender: str(w.sender),
    received_at: str(w.communication_date),
    seen: w.seen === 1 || w.seen === true,
    preview: preview(str(w.content)).slice(0, 240),
  }
}

/**
 * Replies linked to one RFQ, newest first.
 *
 * `sent_or_received: "Received"` matters: our OWN outgoing RFQ and reminder mails are linked to
 * the same RFQ, and listing them back as "replies" would show the purchaser their own message and
 * make an unanswered RFQ look answered.
 */
export function useRfqReplies(rfqId: string | null | undefined) {
  return useQuery({
    queryKey: ['rfq-replies', rfqId ?? ''] as const,
    queryFn: async () => {
      const page = await getResource<WireCommunication>('Communication', {
        filters: [
          ['reference_doctype', '=', 'RFQ'],
          ['reference_name', '=', rfqId ?? ''],
          ['sent_or_received', '=', 'Received'],
        ],
        fields: ['name', 'subject', 'sender', 'communication_date', 'seen', 'content'],
        orderBy: 'communication_date desc',
        limit: 20,
      })
      return page.data.map(toRfqReply)
    },
    enabled: !!rfqId,
  })
}

/**
 * The same replies, for MANY RFQs in one request, grouped by the RFQ they answer.
 *
 * `useRfqReplies` is per-RFQ, which is right for a panel that renders one. The Supplier Quotations
 * TABLE needs the answer for every row at once — specifically so a row can stop saying "Awaiting"
 * and stop offering Remind when a reply is already sitting under it. Deriving that from N separate
 * queries would mean calling a hook inside a `.map()`, which React forbids; one grouped query
 * hands the page a map it can pass down as plain data.
 *
 * `sent_or_received: "Received"` matters here for the same reason it does above: our own outgoing
 * RFQ and reminder mails link to the same RFQ, and counting one would mark an RFQ answered by its
 * own send.
 */
const REPLY_FIELDS = ['name', 'subject', 'sender', 'communication_date', 'seen', 'content', 'reference_name']
/** Explicit: Frappe's own default is 20, which would silently hide replies on a busy enquiry. */
const REPLY_LIMIT = 200

/**
 * Does this mail name `rfqId` outright?
 *
 * An EXACT match on a document number WE issued — not a keyword score and not a model. The caller
 * supplies the ids it may match against (this enquiry's RFQs), so a hit means "this mail names an
 * RFQ that exists on this enquiry", which is identity rather than inference. It answers every
 * supplier who quotes our reference back, which on the live bench is most of them.
 *
 * Word-bounded, so `RFQ-2026-0001` cannot match `RFQ-2026-00012`, and the id is regex-escaped
 * rather than interpolated raw.
 */
export function mentionsRfq(row: Pick<WireCommunication, 'subject' | 'content'>, rfqId: string): boolean {
  if (rfqId.trim() === '') return false
  const pattern = new RegExp(`\\b${rfqId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i')
  return pattern.test(str(row.subject)) || pattern.test(preview(str(row.content)))
}

export function useRepliesByRfq(rfqIds: readonly string[]) {
  // Sorted so that re-ordering the table is not a different cache entry.
  const ids = [...rfqIds].sort()
  return useQuery({
    /* NESTED under `rfq-replies`, not `rfq-replies-grouped`: React Query matches key prefixes
       element by element, so `invalidateQueries({queryKey: ['rfq-replies']})` — which the parse
       action fires — would NOT have matched a top-level `'rfq-replies-grouped'` string. The
       grouped query would have gone stale after reading a reply as a quotation. */
    queryKey: ['rfq-replies', 'grouped', ids] as const,
    queryFn: async () => {
      /*
        TWO queries, because the server links only SOME replies.

        Measured 2026-08-08: supplier Akhil answered RFQ-2026-00013 with the subject
        `RFQ-2026-00013"` — composed fresh rather than hitting reply, so Frappe set no
        `reference_doctype`. Filtering on `reference_name` alone, that quotation was invisible on
        the Supplier Quotations screen, which read "Awaiting" and offered to remind him while his
        prices sat unread in the inbox.

        So the linked query stays — it is certainty — and a second pass sweeps recent UNLINKED
        inbound mail and attaches anything naming one of THIS enquiry's RFQs outright.
      */
      const [linked, unlinked] = await Promise.all([
        getResource<WireCommunication>('Communication', {
          filters: [
            ['reference_doctype', '=', 'RFQ'],
            ['reference_name', 'in', ids],
            ['sent_or_received', '=', 'Received'],
          ],
          fields: REPLY_FIELDS,
          orderBy: 'communication_date desc',
          limit: REPLY_LIMIT,
        }),
        getResource<WireCommunication>('Communication', {
          filters: [
            ['reference_name', 'is', 'not set'],
            ['sent_or_received', '=', 'Received'],
          ],
          fields: REPLY_FIELDS,
          orderBy: 'communication_date desc',
          limit: REPLY_LIMIT,
        }),
      ])

      const byRfq = new Map<string, RfqReply[]>()
      const push = (rfq: string, reply: RfqReply) => {
        const list = byRfq.get(rfq) ?? []
        list.push(reply)
        byRfq.set(rfq, list)
      }

      for (const row of linked.data) {
        const rfq = str(row.reference_name).trim()
        if (rfq !== '') push(rfq, { ...toRfqReply(row), linked: true })
      }

      const alreadySeen = new Set(linked.data.map((row) => str(row.name)))
      for (const row of unlinked.data) {
        if (alreadySeen.has(str(row.name))) continue
        // A mail naming TWO of this enquiry's RFQs is attached to BOTH rather than guessed at: the
        // purchaser sees it under each and decides, which beats silently picking one.
        for (const rfqId of ids) {
          if (mentionsRfq(row, rfqId)) push(rfqId, { ...toRfqReply(row), linked: false })
        }
      }

      return byRfq
    },
    enabled: ids.length > 0,
  })
}

/**
 * Inbound mail carrying this enquiry's `[<enquiry-id>]` tag, whether or not the server linked it.
 *
 * ---- Why this exists alongside `useRfqReplies` ----
 *
 * `useRfqReplies` matches on `reference_name`, which is exact — when the server sets it. It does
 * not always: measured 2026-08-07, a supplier's reply carrying `[ENQ-2026-00019]` in its subject,
 * WITH the filled workbook attached, arrived with `reference_doctype` and `reference_name` both
 * `null`. An earlier reply on the same enquiry was linked. The difference appears to be a true
 * `Re:` reply versus a fresh compose carrying the tag (filed as `D12`).
 *
 * So a reply the purchaser can plainly see in their inbox was invisible to the RFQ it answered.
 *
 * ---- Why this is enquiry-level and not attributed to an RFQ ----
 *
 * The tag names the ENQUIRY, and one enquiry usually has several RFQs out to several suppliers.
 * From the tag alone we know a supplier answered *this enquiry* — not *which RFQ*. Guessing the
 * RFQ (by sender address, say) would attribute a quotation to the wrong supplier, which is worse
 * than showing it unattributed and letting the purchaser say.
 *
 * The RFQ id IS knowable — the server writes it into the workbook (`RFQ ID:` on row 12) and into
 * the attachment's filename — but neither is on the `Communication` row, so reading it would mean
 * downloading and parsing every attachment. Asked for instead (`D12`).
 */
export function useEnquiryReplies(enquiryId: string | null | undefined) {
  return useQuery({
    queryKey: ['enquiry-replies', enquiryId ?? ''] as const,
    queryFn: async () => {
      const page = await getResource<WireCommunication>('Communication', {
        // `like` on the tag, not on the bare id: `ENQ-2026-0001` would otherwise match
        // `ENQ-2026-00019`, and the brackets are what make the tag ours rather than a mention.
        filters: [
          ['sent_or_received', '=', 'Received'],
          ['subject', 'like', `%[${enquiryId ?? ''}]%`],
          // ONLY mail the server did not link. A reply it DID link belongs under its own RFQ,
          // where it can be parsed with certainty; showing it here as well offered a
          // "Read as <supplier>'s quote" button for EVERY supplier on the enquiry, under a
          // heading stating — falsely, for that row — that we don't know which RFQ it answers.
          // One misclick files supplier A's prices as supplier B's quotation, which then drives
          // the allocation. The call site used to pass `linkedIds={[]}`, which excluded nothing.
          ['reference_name', 'is', 'not set'],
        ],
        fields: ['name', 'subject', 'sender', 'communication_date', 'seen', 'content'],
        orderBy: 'communication_date desc',
        limit: 20,
      })
      return page.data.map(toRfqReply)
    },
    enabled: !!enquiryId,
  })
}
